import { Button, List, Tabs, Tag } from 'antd'
import * as dayjs from 'dayjs'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import FadeIn from 'src/components/styled/FadeIn'
import Loading from 'src/components/styled/Loading'
import { fetchOrdersByIds } from 'src/features/OrdersSlice'
import { selectIsIncVat } from 'src/features/VatPreferenceSlice'
import {
  getItemReceiptingPrice,
  getPrice,
  getPriceWithVat,
  getTotalSpendOrderedNoVAT,
  getTotalSpendOrderedWithVAT,
  getTotalSpendReceiptingWithVAT,
} from 'src/utils/priceFunctions'
import {
  getOrderedVsReceived,
  isVisibleOrdersLine,
} from 'src/utils/orderLineVisibility'

import styled from 'styled-components/macro'
import {
  OLLIE_FAB_INLINE_CLEARANCE,
  ollieFabPageClearanceCss,
} from 'src/components/ollie/ollieFabPlacement'

import _ from 'underscore'
import { mapApprovalStatuses } from 'src/utils/approvalStatus'
import useIsBlocked from 'src/hooks/useIsBlocked'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import ReportProductIssueButton from 'src/components/productIssue/ReportProductIssueButton'
import { PRODUCT_ISSUE_SOURCE } from 'src/utils/productIssue'

const { TabPane } = Tabs

const Invoices = () => {
  const venue = useSelector(state => state.venue.venue)
  const account = useSelector(state => state.auth.account)
  const orders = useSelector(state => state.orders.filtered)
  const dateGroupedRedux = useSelector(state => state.orders.dateGrouped)
  const venueSuppliers = useSelector(
    state => state.venueSuppliers.venueSuppliers,
  )
  const [dateGrouped, setDateGrouped] = useState(dateGroupedRedux)
  const dispatch = useDispatch()
  const dateOrdersLoading = useSelector(state => state.orders.dateOrdersLoading)
  const isIncVat = useSelector(selectIsIncVat)

  const isAdmin = account && account.isAdmin
  const dateKeys = Object.keys(dateGrouped).sort((a, b) => b - a)

  useEffect(() => {
    if (orders.length === 0) return
    const key =
      orders[0].orderedDate?.split('T')[0] ||
      moment
        .utc(orders[0].orderedDate)
        .local()
        .startOf('day')
        .format('YYYY-MM-DD')

    setDateGrouped(prev => ({
      ...prev,
      [key]: orders,
    }))
  }, [orders])

  const isQuantityUpdated = item =>
    item.updatedQuantity !== undefined && item.updatedQuantity !== item.quantity

  /**
   * Calculate supplier-level status based on supplierConfirmed aggregation
   * Returns: { label, color } for the supplier status badge
   */
  const calculateSupplierStatus = items => {
    if (!items || items.length === 0) {
      return { label: null, color: null }
    }

    // Check if all items have supplierConfirmed === undefined (Pending)
    const allPending = items.every(
      item =>
        item.supplierConfirmed === undefined ||
        !Object.prototype.hasOwnProperty.call(item, 'supplierConfirmed'),
    )

    if (allPending) {
      return { label: 'Pending', color: 'orange' }
    }

    // Check if any items have supplierConfirmed === false (Rejected)
    const hasRejected = items.some(item => item.supplierConfirmed === false)

    // Check if all items have supplierConfirmed === true (Confirmed)
    const allConfirmed = items.every(item => item.supplierConfirmed === true)

    if (hasRejected) {
      // If any items are rejected, show "Changes" (partial rejection)
      return { label: 'Changes', color: 'magenta' }
    }

    if (allConfirmed) {
      // If all confirmed but some have quantity adjustments, show "Confirmed" with note
      // (The individual items will show the adjustment detail)
      return { label: 'Confirmed', color: 'green' }
    }

    // Mixed state: some confirmed, some pending
    return { label: 'Partially Confirmed', color: 'orange' }
  }

  return (
    <Wrapper>
      <Tabs
        defaultActiveKey={dateKeys[0]}
        onChange={async selectedDate => {
          const existingOrders = dateGrouped[selectedDate]
          const hasValidOrders =
            Array.isArray(existingOrders) &&
            existingOrders.some(order => order.product)

          if (hasValidOrders) return
          const orderIds = (existingOrders || []).map(order => order._id)

          if (!orderIds.length) return
          dispatch(fetchOrdersByIds(orderIds))
        }}
      >
        {dateKeys.map(key => {
          let order = dateGrouped[key]
          order = order.filter(o => o.product)
          const suppliersGrouped = _(order).groupBy(o => {
            const orderNumber = o.purchaseOrderNumber
              ? o.purchaseOrderNumber
              : ''
            return `${o.product.supplier.title}\n${orderNumber}`
          })

          return (
            <TabPane tab={dayjs(key).format('DD/MM/YYYY')} key={key}>
              {dateOrdersLoading ? (
                <Loading />
              ) : (
                <>
                  <ButtonRow>
                    <Link
                      className='ant-btn'
                      to={`/v/${venue._id}/invoice/${dayjs(key).format(
                        'YYYY-MM-DD',
                      )}`}
                    >
                      Download PDF
                    </Link>
                  </ButtonRow>
                  {Object.keys(suppliersGrouped).map(key => {
                    const items = suppliersGrouped[key]
                    // Manager-removed lines stay in DB (qty 0) for audit, but
                    // hide from Orders UI. Receipt-zeroed lines (ordered > 0,
                    // received 0) must stay visible with received = 0.
                    const visibleItems = items.filter(isVisibleOrdersLine)
                    if (visibleItems.length === 0) {
                      return null
                    }

                    const acceptedItems = visibleItems.filter(
                      i => i.supplierConfirmed === true,
                    )

                    const orderedExVat =
                      getTotalSpendOrderedNoVAT(visibleItems) || 0
                    const orderedIncVat =
                      getTotalSpendOrderedWithVAT(visibleItems) || 0
                    const orderedVat = orderedIncVat - orderedExVat

                    const supplierTotals =
                      getTotalSpendReceiptingWithVAT(acceptedItems || []) || {}
                    const supplierIncVat = supplierTotals.totalSpend || 0
                    const supplierVat = supplierTotals.totalVAT || 0
                    const supplierExVat = supplierIncVat - supplierVat

                    const supplierId =
                      visibleItems[0]?.supplier?._id ||
                      visibleItems[0]?.product?.supplier?._id ||
                      null
                    const venueSupplier =
                      supplierId && Array.isArray(venueSuppliers)
                        ? venueSuppliers.find(
                            vs =>
                              vs?.venue === venue._id &&
                              vs?.supplier?._id === supplierId,
                          )
                        : null
                    const isRestrictedSupplier =
                      venueSupplier?.restricted === true

                    const approvalStatuses = visibleItems
                      .map(i => i.approvalStatus)
                      .filter(status => !!status && status !== 'none')

                    const approvalState = mapApprovalStatuses(approvalStatuses)

                    // Calculate supplier status based on approvalStatus (for restricted suppliers)
                    // or supplierConfirmed aggregation for remaining (visible) lines only
                    let supplierStatusLabel = null
                    let supplierStatusColor = null

                    // Priority: approvalStatus takes precedence for restricted suppliers
                    if (approvalState === 'approvalPending') {
                      supplierStatusLabel = 'Manager Approval Pending'
                      supplierStatusColor = 'orange'
                    } else if (approvalState === 'approvalRejected') {
                      supplierStatusLabel = 'Manager Rejected'
                      supplierStatusColor = 'red'
                    } else if (approvalState === 'approved') {
                      // Manager approved - check supplier confirmation status
                      const allSupplierConfirmed = visibleItems.every(
                        item => item.supplierConfirmed === true,
                      )
                      const anySupplierRejected = visibleItems.some(
                        item => item.supplierConfirmed === false,
                      )
                      const allAwaitingSupplier = visibleItems.every(
                        item =>
                          item.supplierConfirmed === undefined ||
                          !Object.prototype.hasOwnProperty.call(
                            item,
                            'supplierConfirmed',
                          ),
                      )

                      if (allSupplierConfirmed) {
                        // All items confirmed by supplier
                        supplierStatusLabel = 'Confirmed'
                        supplierStatusColor = 'green'
                      } else if (allAwaitingSupplier) {
                        // All items awaiting supplier confirmation
                        supplierStatusLabel = visibleItems.some(
                          item => item.approvalHasQuantityChanges,
                        )
                          ? 'Manager Approved with Changes - Awaiting Supplier'
                          : 'Manager Approved - Awaiting Supplier'
                        supplierStatusColor = 'cyan'
                      } else if (anySupplierRejected) {
                        // Some items rejected by supplier
                        supplierStatusLabel = 'Changes'
                        supplierStatusColor = 'magenta'
                      } else {
                        // Mixed: some confirmed, some pending
                        supplierStatusLabel = 'Partially Confirmed'
                        supplierStatusColor = 'orange'
                      }
                    } else {
                      // Fall back to supplierConfirmed aggregation for all suppliers
                      const supplierStatus =
                        calculateSupplierStatus(visibleItems)
                      supplierStatusLabel = supplierStatus.label
                      supplierStatusColor = supplierStatus.color
                    }

                    const requiredDeliveryDay = dayjs(
                      visibleItems[0].requiredDeliveryDay,
                    )

                    const placedVia = visibleItems[0].placedVia
                    const placedBy = visibleItems[0].placedBy

                    // Prefer a human-friendly "who ordered" label:
                    // 1) placedVia when it looks like a person name (new behaviour)
                    // 2) fall back to placedBy (may be name or email on older orders)
                    // 3) finally fall back to placedVia or venue-code message
                    const genericSources = [
                      'dashboard',
                      'mobile',
                      'Email',
                      'Order placed manually on the dashboard',
                    ]
                    const isGenericSource =
                      typeof placedVia === 'string' &&
                      genericSources.includes(placedVia)

                    const displayWho =
                      placedVia && !isGenericSource
                        ? placedVia
                        : placedBy || placedVia || null

                    return (
                      <div key={key}>
                        <ListComp
                          header={
                            <>
                              <Header>
                                <OrderKey>
                                  {key}{' '}
                                  {isRestrictedSupplier && (
                                    <Tag
                                      color='orange'
                                      style={{ marginLeft: 8 }}
                                    >
                                      Restricted – manager approval required
                                    </Tag>
                                  )}
                                  {visibleItems.some(
                                    item => item.approvalHasQuantityChanges,
                                  ) && (
                                    <Tag color='blue' style={{ marginLeft: 8 }}>
                                      Approved with changes
                                    </Tag>
                                  )}
                                  {isAdmin && (
                                    <a
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      href={`/checklist/${
                                        visibleItems[0].venue
                                      }/${visibleItems[0].supplier}/${dayjs(
                                        visibleItems[0].orderedDate,
                                      ).format('YYYY-MM-DD')}/${
                                        visibleItems[0].purchaseOrderNumber
                                          ? encodeURIComponent(
                                              visibleItems[0]
                                                .purchaseOrderNumber,
                                            )
                                          : 'all'
                                      }`}
                                    >
                                      <Button
                                        style={{
                                          marginLeft: 46,
                                          marginRight: 46,
                                          marginTop: 1,
                                        }}
                                      >
                                        Checklist
                                      </Button>
                                    </a>
                                  )}
                                </OrderKey>

                                <h4>
                                  {displayWho
                                    ? `Order placed by: ${displayWho}`
                                    : 'Order placed using venue code'}
                                </h4>
                                {supplierStatusLabel && (
                                  <Tag color={supplierStatusColor}>
                                    {supplierStatusLabel}
                                  </Tag>
                                )}

                                <h4 style={{ marginLeft: 12 }}>
                                  {requiredDeliveryDay.isValid()
                                    ? `Delivery date: ${requiredDeliveryDay.format(
                                        'DD-MM-YYYY',
                                      )}`
                                    : 'Next available delivery day'}
                                </h4>
                              </Header>
                            </>
                          }
                          footer={
                            <div>
                              <h4>
                                Ordered (ex VAT): £{orderedExVat.toFixed(2)}
                              </h4>
                              <h4>VAT on order: £{orderedVat.toFixed(2)}</h4>
                              <h4>
                                Ordered total (inc VAT): £
                                {orderedIncVat.toFixed(2)}
                              </h4>
                              <br />
                              <h4>
                                Supplier confirmed amount (ex VAT): £
                                {supplierExVat.toFixed(2)}
                              </h4>
                              <h4>
                                VAT charged by supplier: £
                                {supplierVat.toFixed(2)}
                              </h4>
                              <h4>
                                Supplier confirmed total (inc VAT): £
                                {supplierIncVat.toFixed(2)}
                              </h4>
                            </div>
                          }
                          bordered
                          dataSource={visibleItems}
                          renderItem={item => {
                            const { ordered, received, hasDiscrepancy } =
                              getOrderedVsReceived(item)
                            const quantityTagColor = 'orange'
                            const hasVat =
                              item.hasVat !== undefined
                                ? item.hasVat
                                : item.product?.hasVat

                            let statusText = ''
                            let statusVariant = 'default'

                            // Check manager approval status first (for restricted suppliers)
                            if (item.approvalStatus === 'pending') {
                              statusText = 'Manager Approval Pending'
                              statusVariant = 'pending'
                            } else if (
                              item.approvalStatus === 'rejected' ||
                              item.approvalStatus === 'auto_rejected'
                            ) {
                              statusText = 'Manager Rejected'
                              statusVariant = 'rejected'
                            } else if (item.approvalStatus === 'approved') {
                              // Manager approved - check supplier confirmation status
                              if (item.supplierConfirmed === undefined) {
                                statusText = item.approvalHasQuantityChanges
                                  ? 'Manager Approved with Changes - Awaiting Supplier'
                                  : 'Manager Approved - Awaiting Supplier'
                                statusVariant = 'managerApprovedPending'
                              } else if (item.supplierConfirmed === true) {
                                if (isQuantityUpdated(item)) {
                                  statusText = 'Quantity Adjusted'
                                  statusVariant = 'adjusted'
                                } else {
                                  statusText = 'Confirmed'
                                  statusVariant = 'confirmed'
                                }
                              } else if (item.supplierConfirmed === false) {
                                statusText = 'Supplier Rejected'
                                statusVariant = 'rejected'
                              }
                            } else {
                              // No manager approval required - check supplier confirmation directly
                              if (item.supplierConfirmed === undefined) {
                                statusText = 'Pending'
                                statusVariant = 'pending'
                              } else if (item.supplierConfirmed === true) {
                                if (isQuantityUpdated(item)) {
                                  statusText = 'Quantity Adjusted'
                                  statusVariant = 'adjusted'
                                } else {
                                  statusText = 'Confirmed'
                                  statusVariant = 'confirmed'
                                }
                              } else if (item.supplierConfirmed === false) {
                                statusText = 'Rejected'
                                statusVariant = 'rejected'
                              }
                            }

                            return (
                              <List.Item>
                                <Tag color={quantityTagColor}>
                                  Ordered: {ordered}
                                </Tag>
                                {hasDiscrepancy && (
                                  <Tag color='red'>Received: {received}</Tag>
                                )}
                                {item.title || item.product.title} @ £
                                {isIncVat
                                  ? getPriceWithVat(item)
                                  : getPrice(item)}{' '}
                                {hasVat && (
                                  <Tag
                                    color='default'
                                    style={{
                                      marginLeft: 8,
                                      fontSize: 12,
                                      color: '#000',
                                    }}
                                  >
                                    VAT
                                  </Tag>
                                )}
                                <br />
                                {hasDiscrepancy && (
                                  <span>
                                    Please note: You ordered {ordered} but only{' '}
                                    {received} received
                                  </span>
                                )}
                                <h5 style={{ marginLeft: '34px' }}>
                                  SKU: {item.product.sku}
                                </h5>
                                <h4>
                                  Order Status:{' '}
                                  <StatusText $variant={statusVariant}>
                                    {statusText}
                                  </StatusText>
                                </h4>
                                <h4>
                                  Price: £{getItemReceiptingPrice(item)} inc.
                                  VAT
                                </h4>
                                <ReportProductIssueButton
                                  venueId={venue?._id}
                                  order={item}
                                  product={item.product}
                                  sourceScreen={PRODUCT_ISSUE_SOURCE.ORDERS}
                                />
                              </List.Item>
                            )
                          }}
                        />
                      </div>
                    )
                  })}
                </>
              )}
            </TabPane>
          )
        })}
      </Tabs>
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)`
  ${ollieFabPageClearanceCss}

  .ant-tabs-bar {
    padding-right: ${OLLIE_FAB_INLINE_CLEARANCE}px;
  }
`

const ListComp = styled(List)`
  margin-bottom: 15px !important;
  background-color: ${({ theme }) => theme.colours.mayoWhite} !important;
`

const ButtonRow = styled.div`
  margin-bottom: 18px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`
const OrderKey = styled.h3`
  white-space: pre-wrap;
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  min-width: min(250px, 100%);
`

const StatusText = styled.span`
  font-weight: 600;
  color: ${({ theme, $variant }) => {
    if ($variant === 'pending') return theme.colours.orange
    if ($variant === 'rejected') return theme.colours.chilliRed
    if ($variant === 'confirmed') return theme.colours.avocadoGreen
    if ($variant === 'managerApprovedPending') return '#13C2C2' // cyan color for manager approved awaiting supplier
    if ($variant === 'adjusted') return theme.colours.orange
    return theme.colours.bbqBlack
  }};
`

const GatedOrders = () => {
  const blocked = useIsBlocked('orders')
  if (blocked) return <PermissionBlockScreen />
  return <Invoices />
}

export default GatedOrders
